import React from "react"
import styled from "styled-components"
import PropTypes from "prop-types"
import theme from "@src/styles/theme"

const ButtonLink = styled.a`
  text-decoration: none;
`

const ButtonStyle = styled.div`
  display: flex;
  width: 100%;
  min-width: max-content;
  height: 50px;
  padding: 10px 18px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  border-radius: ${props => props.$radius || "8px"};
  background: ${props => props.$background};
  box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.05);
  color: ${props => props.$color};
  ${props => props.$borderColor && `border: 1.5px solid ${props.$borderColor}`};
  ${props => props.$weight && `font-weight: ${props.$weight};`}
  cursor: pointer;
  text-decoration: none;

  p {
    margin: 0;
  }
  &:hover {
    filter: brightness(1.1);
  }
`

function Button({
  text,
  type = "light",
  className,
  onClick,
  to,
  children,
  disabled = true,
  radius,
}) {
  const themes = {
    dark: {
      background: theme.secondary900,
      color: theme.whiteShade,
    },
    light: {
      background: theme.whiteShade,
      color: theme.secondary900,
    },
    lightBold: {
      background: theme.whiteShade,
      color: theme.secondary900,
      weight: 700,
      borderColor: theme.secondary800,
    },
    yellow: {
      background: theme.primary400,
      color: theme.secondary900,
      weight: 700,
    },
    greenShade: {
      background: theme.greenShade,
      color: theme.whiteShade,
    },
    lightGreen: {
      background: theme.secondary600,
      color: theme.whiteShade,
    },
    review: {
      background: theme.secondary200,
      color: theme.black,
      weight: 600,
    },
    outline: {
      background: theme.whiteShade,
      color: theme.secondary800,
      borderColor: theme.secondary800,
    },
    outlineQuestion: {
      background: theme.whiteShade,
      color: theme.blackShade,
      borderColor: theme.blackShade,
      weight: 600,
    },
    black: {
      background: theme.blackShade,
      color: theme.whiteShade,
      borderColor: theme.blackShade,
    },
    cover: {
      background: theme.black,
      color: theme.cover,
      borderColor: theme.cover,
    },
  }

  const buttonProps = {
    $background: themes[type].background,
    $color: themes[type].color,
    $borderColor: themes[type].borderColor,
    $weight: themes[type].weight,
    $radius: radius,
    className: `button ${className || ""}`,
    onClick: disabled && onClick,
  }

  return to ? (
    <ButtonLink href={to}>
      <ButtonStyle {...buttonProps}>{text || children}</ButtonStyle>
    </ButtonLink>
  ) : (
    <ButtonStyle {...buttonProps}>{text || children}</ButtonStyle>
  )
}

Button.propTypes = {
  className: PropTypes.string,
  text: PropTypes.string.isRequired,
  type: PropTypes.oneOf([
    "black",
    "light",
    "lightBold",
    "dark",
    "yellow",
    "review",
    "greenShade",
    "lightGreen",
    "outline",
    "outlineQuestion",
    "cover",
  ]),
  onClick: PropTypes.func,
  to: PropTypes.string,
  radius: PropTypes.string,
  disabled: PropTypes.bool,
  children: PropTypes.node,
}

export default Button
